import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Save } from "lucide-react";
import CounterpartyManager from "../portal/CounterpartyManager";
import { ModuleProfileCard } from "../portal/ModuleProfile";

const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";

const emptyProfile = {
  india_enabled: true,
  india_role: "producer",
  india_stream: "plastic_packaging",
  india_registration_status: "unknown",
  india_registration_number: "",
  exporter_enabled: false,
  export_markets: "",
  export_notes: "",
};

function profileToDraft(profile) {
  const india = profile?.india || {};
  const exporter = profile?.exporter || {};
  return {
    india_enabled: Boolean(india.enabled),
    india_role: india.epr_role || "producer",
    india_stream: india.stream || "plastic_packaging",
    india_registration_status: india.registration_status || "unknown",
    india_registration_number: india.registration_number || "",
    exporter_enabled: Boolean(exporter.enabled),
    export_markets: (exporter.markets || []).join(", "),
    export_notes: exporter.notes || "",
  };
}

export default function AdminNetworks({ api }) {
  const [orgs, setOrgs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState(emptyProfile);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api("/admin/orgs");
    const items = data.items || [];
    setOrgs(items);
    if (!selectedId && items.length) {
      const preferred = items.find((o) => o.legal_name.toLowerCase().includes("disha")) || items[0];
      setSelectedId(preferred.id);
    }
  }, [api, selectedId]);

  useEffect(() => {
    load().catch((e) => toast.error(e.message));
  }, [load]);

  const selectedOrg = useMemo(() => orgs.find((o) => o.id === selectedId), [orgs, selectedId]);

  useEffect(() => {
    if (selectedOrg) setDraft(profileToDraft(selectedOrg.module_profile));
  }, [selectedOrg]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setBusy(true);
    try {
      await api(`/admin/orgs/${selectedOrg.id}/module-profile`, {
        method: "PATCH",
        body: JSON.stringify({
          ...draft,
          export_markets: draft.export_markets.split(",").map((v) => v.trim()).filter(Boolean),
        }),
      });
      toast.success("Module profile saved");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8" data-testid="admin-networks">
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-emerald-700" />
        <h2 className="font-display text-lg font-bold text-forest-deep">Client networks</h2>
      </div>
      <p className="mt-1 text-sm text-forest-deep/60">Configure each client’s India and export tracks, then maintain their supplier and buyer compliance board.</p>

      <div className="mt-5 rounded-2xl border border-emerald-950/10 bg-white p-4 shadow-sm">
        <label className="block text-sm font-semibold text-forest-deep/75">Client organisation</label>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className={`${inputCls} mt-2 max-w-xl`} data-testid="admin-network-org-select">
          {orgs.map((org) => <option key={org.id} value={org.id}>{org.legal_name}</option>)}
        </select>
      </div>

      {selectedOrg && (
        <>
          <div className="mt-5">
            <ModuleProfileCard profile={selectedOrg.module_profile} />
          </div>

          <form onSubmit={saveProfile} className="mt-5 rounded-3xl border border-emerald-950/10 bg-white p-5 shadow-sm" data-testid="module-profile-form">
            <h3 className="font-display text-base font-bold text-forest-deep">Module profile</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <label className="flex items-center gap-2 rounded-xl bg-parchment-base px-3 py-2 text-sm font-semibold text-forest-deep">
                <input type="checkbox" checked={draft.india_enabled} onChange={(e) => setDraft((d) => ({ ...d, india_enabled: e.target.checked }))} className="h-4 w-4 accent-emerald-700" data-testid="profile-india-enabled" />
                India module
              </label>
              <select value={draft.india_role} onChange={(e) => setDraft((d) => ({ ...d, india_role: e.target.value }))} className={inputCls} data-testid="profile-india-role">
                <option value="producer">Producer</option>
                <option value="importer">Importer</option>
                <option value="brand_owner">Brand owner</option>
              </select>
              <select value={draft.india_stream} onChange={(e) => setDraft((d) => ({ ...d, india_stream: e.target.value }))} className={inputCls} data-testid="profile-india-stream">
                <option value="plastic_packaging">Plastic packaging</option>
                <option value="e_waste">E-waste</option>
                <option value="battery">Battery</option>
                <option value="tyre">Tyre</option>
                <option value="used_oil">Used oil</option>
                <option value="other">Other</option>
              </select>
              <select value={draft.india_registration_status} onChange={(e) => setDraft((d) => ({ ...d, india_registration_status: e.target.value }))} className={inputCls} data-testid="profile-india-status">
                <option value="registered">CPCB registered</option>
                <option value="not_registered">Not registered</option>
                <option value="pending">Pending</option>
                <option value="unknown">Unknown</option>
              </select>
              <input placeholder="CPCB registration number" value={draft.india_registration_number} onChange={(e) => setDraft((d) => ({ ...d, india_registration_number: e.target.value }))} className={`${inputCls} lg:col-span-2`} data-testid="profile-india-registration" />
              <label className="flex items-center gap-2 rounded-xl bg-parchment-base px-3 py-2 text-sm font-semibold text-forest-deep">
                <input type="checkbox" checked={draft.exporter_enabled} onChange={(e) => setDraft((d) => ({ ...d, exporter_enabled: e.target.checked }))} className="h-4 w-4 accent-emerald-700" data-testid="profile-exporter-enabled" />
                Exporter
              </label>
              <input placeholder="Export markets, comma separated" value={draft.export_markets} onChange={(e) => setDraft((d) => ({ ...d, export_markets: e.target.value }))} className={inputCls} data-testid="profile-export-markets" />
              <textarea placeholder="Export notes" value={draft.export_notes} onChange={(e) => setDraft((d) => ({ ...d, export_notes: e.target.value }))} className={`${inputCls} min-h-[92px] lg:col-span-4`} data-testid="profile-export-notes" />
            </div>
            <button type="submit" disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white hover:bg-forest-rich disabled:opacity-60" data-testid="profile-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save module profile
            </button>
          </form>

          <CounterpartyManager key={selectedOrg.id} api={api} basePath={`/admin/orgs/${selectedOrg.id}/counterparties`} canEdit orgName={selectedOrg.legal_name} />
        </>
      )}
    </section>
  );
}
